import { useEffect, useMemo } from 'react';
import { Target } from 'lucide-react';
import { QuizPlayer } from '@/components/quiz-player';
import { QuizSlots, QuizWidgetActivity } from './types';
import { useQuizContext } from './hooks/use-quiz-context';
import { ACTIVITY_SELECTOR } from './hooks/use-quiz-controller';
import { QuizDisplayActivity } from './screens/quiz-display-activity';

export type QuizDisplayProps = React.ComponentProps<'div'> & {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: 'default' | 'contained' | 'ghost';
  defaultScreen?: 'welcome' | 'quiz';
  slots?: QuizSlots;
  disabled?: string[] | ((data: QuizWidgetActivity[]) => string[]);
  autoselect?: boolean;
  displayStrategy?: 'auto' | 'activity' | 'quiz';
  displayActivity?: (data: QuizWidgetActivity[]) => QuizWidgetActivity | null;
};

export function QuizDisplay({
  // player props
  title,
  description,
  variant,
  defaultScreen,
  // own props
  className,
  slots = {},
  autoselect = true,
  displayStrategy = 'auto',
  displayActivity,
  disabled,
  // slotProps = {},
  ...others
}: QuizDisplayProps) {
  const { Loading, Empty, DisplayActivityScreen = QuizDisplayActivity } = slots;

  const controller = useQuizContext();

  const {
    loading,
    quiz,
    data,
    activity,
    selectActivity,
    events,
    attempts,
    questions,
    requireConfirm,
    displayIndex,
    // validate,
  } = controller;

  // The display activity can be controlled by props.
  // This is the activity we should render in that case
  const displayedActivity = useMemo(() => {
    if (displayActivity) {
      return displayActivity(data);
    }

    return autoselect ? ACTIVITY_SELECTOR.withLessAttempt(data) : null;
  }, [displayActivity, autoselect, data]);

  // calculate disabled activities. It only used when DisplayActivityScreen is used
  const disabledActivities = useMemo(() => {
    if (!disabled) return [];

    const getDisabled = Array.isArray(disabled) ? () => disabled : disabled;
    return getDisabled(data);
  }, [disabled, data]);

  // calculate the display screen based on the provided strategy
  // and the current controller state
  const displayScreen = useMemo(() => {
    if (displayStrategy !== 'auto') return displayStrategy;

    return activity || displayedActivity ? 'quiz' : 'activity';
  }, [activity, displayStrategy, displayedActivity]);

  useEffect(() => {
    // if a displayed activity is provided via props
    // we auto select it here
    if (displayedActivity) {
      selectActivity(displayedActivity.id);
    }
  }, [displayedActivity, selectActivity]);

  // We set this flag to know when we need to wait react to update the
  // current activity state
  const loadingDisplayed = displayedActivity && !activity;

  console.log({
    displayStrategy,
    loadingDisplayed,
    quiz,
    displayedActivity,
    loading,
  });
  // Show the loader when activity data is loading. To prevent "blink"
  // effect of the empty screen we keep showing the loader while
  // we're selecting the current activity
  if (loading || (displayScreen === 'quiz' && loadingDisplayed)) {
    return Loading ? <Loading /> : null;
  }

  // Render the empty state when there is no data about activities for
  // the provided reference. The empty state is also shown in case
  // the quiz screen is set and there is no current activity selected
  if (!data.length || (displayScreen === 'quiz' && !activity)) {
    return Empty ? <Empty /> : null;
  }

  // in case there is no activity selected
  // and we do not have any displayed activity specified via props
  // we can safely fallback to the display activity screen
  if (displayScreen === 'activity') {
    return (
      <DisplayActivityScreen
        data={data}
        onActivitySelect={selectActivity}
        disabled={disabledActivities}
      />
    );
  }

  return (
    <QuizPlayer
      {...others}
      title={title || quiz?.name}
      description={description || quiz?.description}
      questions={questions}
      events={events}
      attempts={attempts}
      // validate={validate}
      variant={variant}
      defaultScreen={defaultScreen}
      requireConfirm={requireConfirm}
      displayIndex={displayIndex}
      slots={slots}
      slotProps={{
        welcome: {
          details: [
            {
              label: `${attempts.length} Tentativi precedenti`,
              icon: <Target />,
            },
          ],
        },
      }}
    />
  );
}
